/**
 * Replay-tab state. Holds the loaded timeline + a run loop that fires each
 * step through the live OcppV16Adapter and pauses on the first CALLERROR.
 *
 * The store keeps the protocol layer out of UI scope by accepting an
 * action-sender callback at run time (closed over the adapter by the
 * caller). That keeps state/ free of any direct adapter dependency too.
 *
 * Session 8f.8:
 *   - NIT-015: speed enum extended (instant / 10x / 1x / realtime) and
 *     Pause/Resume implemented via an internal promise resolver.
 *   - FEAT-009: setSkippedAll / setSkippedAbove / setSkippedBelow bulk
 *     actions for partial-range replay.
 */
import { create } from 'zustand';
import { applyTxIdRemap, buildTimeline, cloneJson, type ReplayStep } from './replayTimeline';

export type ReplaySpeed = 'instant' | '10x' | '1x' | 'realtime';

/** NIT-015 — per-speed gap (ms) between steps. Realtime is computed per-step. */
export const SPEED_GAP_MS: Record<Exclude<ReplaySpeed, 'realtime'>, number> = {
  instant: 0,
  '10x': 50,
  '1x': 200,
};

/** Cap real-time waits — avoids 5-minute heartbeat pauses inside Play. */
export const REALTIME_MAX_GAP_MS = 30_000;

export interface ReplaySendResult {
  /** CSMS-assigned transactionId, if the response carries one. */
  readonly transactionId?: number;
}

/**
 * Fire a single CALL through the live adapter. Throws on CALLERROR /
 * validation failure / timeout — the run loop catches those and marks the
 * step `failed`.
 */
export type ReplaySender = (
  action: string,
  payload: Record<string, unknown>,
) => Promise<ReplaySendResult>;

export interface ReplayStoreState {
  readonly steps: readonly ReplayStep[];
  readonly status: 'idle' | 'loaded' | 'running' | 'paused' | 'done' | 'failed';
  readonly currentIndex: number;
  readonly loadError: string | null;
  /** Map of historical → live transactionId, populated as Start replies arrive. */
  readonly remap: ReadonlyMap<number, number>;
  /** FEAT-009 — currently selected row (used by bulk skip-above/below). */
  readonly selectedSeq: number | null;
}

export interface ReplayStoreActions {
  load(raw: unknown): void;
  toggleSkip(seq: number): void;
  skipAllOfAction(action: string): void;
  /** FEAT-009 bulk actions. */
  setSkippedAll(skipped: boolean): void;
  setSkippedAbove(seq: number): void;
  setSkippedBelow(seq: number): void;
  selectSeq(seq: number | null): void;
  /** FEAT-025 — overwrite a step's payload (e.g. from the inline editor). */
  setStepPayload(seq: number, payload: Record<string, unknown>): void;
  /** FEAT-025 — restore a step's payload to its frozen originalPayload. */
  revertStepPayload(seq: number): void;
  reset(): void;
  run(sender: ReplaySender, speed: ReplaySpeed): Promise<void>;
  pause(): void;
  resume(): void;
  stop(): void;
}

export type ReplayStore = ReplayStoreState & ReplayStoreActions;

interface RunControl {
  cancelled: boolean;
  paused: boolean;
  /** Resolves when resume() is called. Created lazily on pause. */
  pauseResolver: (() => void) | null;
  pausePromise: Promise<void> | null;
}

export const useReplayStore = create<ReplayStore>((set, get) => {
  let control: RunControl | null = null;

  function patchStep(seq: number, patch: Partial<ReplayStep>): void {
    const next = get().steps.map((s) => (s.seq === seq ? { ...s, ...patch } : s));
    set({ steps: next });
  }

  return {
    steps: [],
    status: 'idle',
    currentIndex: -1,
    loadError: null,
    remap: new Map(),
    selectedSeq: null,

    load(raw: unknown): void {
      try {
        const steps = buildTimeline(raw);
        set({
          steps,
          status: steps.length > 0 ? 'loaded' : 'idle',
          currentIndex: -1,
          loadError:
            steps.length === 0 ? 'No replayable CP-initiated CALLs found in this file.' : null,
          remap: new Map(),
          selectedSeq: null,
        });
      } catch (err) {
        set({
          steps: [],
          status: 'idle',
          loadError: err instanceof Error ? err.message : String(err),
          remap: new Map(),
        });
      }
    },

    toggleSkip(seq: number): void {
      const next = get().steps.map((s) => (s.seq === seq ? { ...s, skipped: !s.skipped } : s));
      set({ steps: next });
    },

    skipAllOfAction(action: string): void {
      const next = get().steps.map((s) => (s.action === action ? { ...s, skipped: true } : s));
      set({ steps: next });
    },

    setSkippedAll(skipped: boolean): void {
      const next = get().steps.map((s) => ({ ...s, skipped }));
      set({ steps: next });
    },

    setSkippedAbove(seq: number): void {
      const next = get().steps.map((s) => (s.seq < seq ? { ...s, skipped: true } : s));
      set({ steps: next });
    },

    setSkippedBelow(seq: number): void {
      const next = get().steps.map((s) => (s.seq > seq ? { ...s, skipped: true } : s));
      set({ steps: next });
    },

    selectSeq(seq: number | null): void {
      set({ selectedSeq: seq });
    },

    setStepPayload(seq: number, payload: Record<string, unknown>): void {
      // FEAT-025 — store the clone so future edits don't accidentally
      // mutate the caller's reference. originalPayload stays untouched.
      const cloned = cloneJson(payload);
      const next = get().steps.map((s) => (s.seq === seq ? { ...s, payload: cloned } : s));
      set({ steps: next });
    },

    revertStepPayload(seq: number): void {
      // FEAT-025 — restore from the frozen snapshot. Clone again so the
      // restored payload is independent from originalPayload (a future
      // setStepPayload mustn't touch the snapshot).
      const next = get().steps.map((s) =>
        s.seq === seq ? { ...s, payload: cloneJson(s.originalPayload) } : s,
      );
      set({ steps: next });
    },

    reset(): void {
      const next = get().steps.map((s) => ({
        ...s,
        skipped: false,
        status: 'pending' as const,
        error: null,
      }));
      set({
        steps: next,
        status: 'loaded',
        currentIndex: -1,
        remap: new Map(),
      });
    },

    async run(sender: ReplaySender, speed: ReplaySpeed): Promise<void> {
      const myControl: RunControl = {
        cancelled: false,
        paused: false,
        pauseResolver: null,
        pausePromise: null,
      };
      control = myControl;
      set({ status: 'running' });

      const remap = new Map(get().remap);
      let prevAt: number | null = null;

      for (let i = 0; i < get().steps.length; i++) {
        if (myControl.cancelled) break;
        // Pause gate — await the resume promise before proceeding.
        if (myControl.paused && myControl.pausePromise) {
          set({ status: 'paused' });
          await myControl.pausePromise;
          if (myControl.cancelled) break;
          set({ status: 'running' });
        }
        const step = get().steps[i];
        if (!step) break;
        set({ currentIndex: i });
        if (step.skipped) {
          patchStep(step.seq, { status: 'skipped' });
          continue;
        }
        const remappedPayload = applyTxIdRemap(step.payload, remap);
        patchStep(step.seq, { status: 'running', error: null });
        try {
          const result = await sender(step.action, remappedPayload);
          const originalTxRaw = step.payload['_originalTransactionId'];
          if (
            step.action === 'StartTransaction' &&
            result.transactionId !== undefined &&
            typeof originalTxRaw === 'number'
          ) {
            remap.set(originalTxRaw, result.transactionId);
          } else if (step.action === 'StartTransaction' && result.transactionId !== undefined) {
            remap.set(result.transactionId, result.transactionId);
          }
          set({ remap: new Map(remap) });
          patchStep(step.seq, { status: 'done' });
        } catch (err) {
          patchStep(step.seq, {
            status: 'failed',
            error: err instanceof Error ? err.message : String(err),
          });
          set({ status: 'failed' });
          control = null;
          return;
        }
        // NIT-015 — speed-controlled gap before the next step.
        if (!myControl.cancelled) {
          const gap = computeGap(speed, prevAt, step.originalCreatedAt);
          prevAt = step.originalCreatedAt;
          if (gap > 0) {
            await new Promise<void>((resolve) => setTimeout(resolve, gap));
          }
        }
      }
      if (myControl.cancelled) {
        set({ status: 'loaded' });
      } else {
        set({ status: 'done' });
      }
      control = null;
    },

    pause(): void {
      if (!control || control.paused) return;
      control.paused = true;
      control.pausePromise = new Promise<void>((resolve) => {
        if (!control) return;
        control.pauseResolver = resolve;
      });
    },

    resume(): void {
      if (!control || !control.paused) return;
      control.paused = false;
      const r = control.pauseResolver;
      control.pauseResolver = null;
      control.pausePromise = null;
      r?.();
    },

    stop(): void {
      if (!control) return;
      control.cancelled = true;
      // If currently paused, releasing the promise unblocks the cancel path.
      const r = control.pauseResolver;
      control.pauseResolver = null;
      control.pausePromise = null;
      control.paused = false;
      r?.();
    },
  };
});

/**
 * Gap (ms) before sending the next step under a given speed. `prevAt` is
 * the previous step's `originalCreatedAt` (or null for the first step);
 * `nextAt` is the current step's. Real-time honours the delta, clamped
 * by REALTIME_MAX_GAP_MS so a 5-minute heartbeat gap doesn't freeze Play.
 */
export function computeGap(
  speed: ReplaySpeed,
  prevAt: number | null,
  nextAt: number,
): number {
  if (speed === 'realtime') {
    if (prevAt === null) return 0;
    const delta = nextAt - prevAt;
    if (!Number.isFinite(delta) || delta <= 0) return 0;
    return Math.min(delta, REALTIME_MAX_GAP_MS);
  }
  return SPEED_GAP_MS[speed];
}
